import { FormInstance } from 'antd';
import { formatHTS } from '@xbcb/hts-client-utils';
import { NamePath } from '@xbcb/ui-types';

/**
 * `unitAssistPercentage` is also used to carry the EU additional duty rate on these
 * chapter 99 tariffs, where it does not represent an assist. See LineItemHTSSummaryV2,
 * which reads it back out as `euAdditionalDutyRate`. Never derive assist from it there.
 */
export const EU_ADDITIONAL_DUTY_RATE_HTS_NUMBERS = ['9903.02.19', '9903.02.20'];

/**
 * Whether an HTS number carries the EU additional duty rate rather than an assist.
 *
 * Callers hold the HTS number in both the dotted form taken straight off the form and
 * the period-stripped form produced by formatHTS, so both sides are normalized here.
 * @param {string} [htsNumber] - The HTS number to test, dotted or period-stripped
 * @return {boolean} True when the number is an EU additional duty rate tariff
 */
export const isEuAdditionalDutyRateHts = (htsNumber?: string): boolean =>
  Boolean(htsNumber) &&
  EU_ADDITIONAL_DUTY_RATE_HTS_NUMBERS.some(
    (euHtsNumber) => formatHTS(euHtsNumber) === formatHTS(htsNumber),
  );

/**
 * Convert an assist percentage of the unit value into the unit and total assist.
 * Shared with UnitAssistInput so the entry form and the product-load path cannot
 * drift apart.
 * @param {number} input.percentage - The assist percentage of the unit value
 * @param {number} input.unitValue - The unit value the percentage applies to
 * @param {number} input.quantity - The line quantity
 * @param {object} input
 * @return {{unitAssist: number, totalAssist: number}} The derived unit and total assist
 */
export const calculateAssistFromPercentage = ({
  percentage,
  unitValue,
  quantity,
}: {
  percentage: number;
  unitValue: number;
  quantity: number;
}): { unitAssist: number; totalAssist: number } => {
  const unitAssist = (percentage / 100) * unitValue;
  return { unitAssist, totalAssist: unitAssist * quantity };
};

type SetAssistFromPercentageProps = {
  form: FormInstance;
  productNamePath: NamePath;
  currencyNamePath?: NamePath;
  realTariffIndex?: number;
};

/**
 * Derive the unit assist and total assist from `unitAssistPercentage`.
 *
 * Products can be uploaded with an assist percentage and no assist value, and the
 * value is only knowable once the line has both a unit value and a quantity. The
 * percentage is the source of truth, so this recalculates rather than only filling a
 * blank — call it whenever the percentage, unit value or quantity changes.
 *
 * A percentage of 0 is left alone: absent assist is already the correct outcome, and
 * writing a 0 assist value would trip the "negative number or zero" validation.
 */
export const setAssistFromPercentage = ({
  form,
  productNamePath,
  currencyNamePath,
  realTariffIndex,
}: SetAssistFromPercentageProps): void => {
  if (realTariffIndex === undefined) return;

  const tariffNamePath = [
    ...productNamePath,
    'lines',
    0,
    'tariffs',
    realTariffIndex,
  ];

  const htsNumber = form.getFieldValue([...tariffNamePath, 'htsNumber']);
  if (isEuAdditionalDutyRateHts(htsNumber)) return;

  const percentage = Number(
    form.getFieldValue([...tariffNamePath, 'unitAssistPercentage']),
  );
  const unitValue = Number(
    form.getFieldValue([...tariffNamePath, 'unitValue', 'value']),
  );
  const quantity = Number(form.getFieldValue([...productNamePath, 'quantity']));

  // unitValue and quantity are divisors/multipliers that make the result meaningless
  // when absent or zero, so wait until the line is keyed rather than writing a 0.
  if (!Number.isFinite(percentage) || !percentage) return;
  if (!Number.isFinite(unitValue) || !unitValue) return;
  if (!Number.isFinite(quantity) || !quantity) return;

  const { unitAssist, totalAssist } = calculateAssistFromPercentage({
    percentage,
    unitValue,
    quantity,
  });
  const currency = currencyNamePath
    ? form.getFieldValue(currencyNamePath)
    : undefined;

  form.setFieldValue([...tariffNamePath, 'unitAssist'], {
    value: unitAssist,
    currency,
  });
  form.setFieldValue([...productNamePath, 'totalAssist'], {
    value: totalAssist,
    currency,
  });
};
